import { registerClass } from '../../SatCoreRegistry';

import { DEFAULT_PLACEMENT_CUT_SCORES } from '../../constants';
import gradebookManager from '../../managers/GradebookManager';

export default class GradebookPlacementService {
  /**
   * Evaluate a raw cumulative score against a placement cut-score table and return
   * the matching rule object, or null if the score is null/undefined.
   *
   * Rules must be sorted ascending by `max`. An entry with `max: null` is treated
   * as unbounded (matches any score above the previous entry's max).
   *
   * `customPlacementCutScores` (from the satellite layout) may be:
   *   - An object keyed by tag name: { "5e:K-1": [...rules], "5e:2-3": [...] }
   *     In this case `tagName` (the `selected` tag from viewContentStandardsAndTags)
   *     is used to pick the correct rule set.
   *   - A flat array (legacy): rules are used directly regardless of `tagName`.
   *
   * Falls back to DEFAULT_PLACEMENT_CUT_SCORES when no match is found.
   *
   * @param {number|null|undefined} score   - raw cumulative score
   * @param {string|null} [tagName]         - selected tag name from the content item
   * @returns {{ code: string, label: string, borderline: boolean } | null}
   */
  static getPlacement(score, tagName) {
    const cutScores = gradebookManager.customPlacementCutScores ?? DEFAULT_PLACEMENT_CUT_SCORES;
    let activeRules;
    if (Array.isArray(cutScores)) {
      activeRules = cutScores;
    } else if (cutScores && typeof cutScores === 'object') {
      activeRules = tagName ? (cutScores[tagName] ?? null) : null;
    } else {
      activeRules = DEFAULT_PLACEMENT_CUT_SCORES;
    }
    if (score == null || !Array.isArray(activeRules) || activeRules.length === 0) {
      return null;
    }
    return activeRules.find((r) => r.max === null || score <= r.max) ?? null;
  }

  /**
   * Convenience: return just the display label for a score, or null if no match.
   */
  static getPlacementLabel(score, tagName) {
    return GradebookPlacementService.getPlacement(score, tagName)?.label ?? null;
  }
}

registerClass('GradebookPlacementService', GradebookPlacementService);
